import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useGetAiPromptLazyQuery } from "../../../../__generated__/api";
import Confirm from "../../../../components/confirm/Confirm";
import { Json } from "./base";
import AIQuestion, { AvailableActionResponseAction } from "../../../../components/artificial-intelligence/AIQuestion";
import SCHEMA from "./schema.json";

import Styles from "./CreateService.module.scss";

const AVAILABLE_ACTIONS = [
    {
        id: "write-schema",
        name: "Writes the entire schema",
        prompt: "Overwrites everything in the schema with the new schema",
        parameters: [
            {
                name: "schema",
                description: "The new schema",
            },
        ],
        invoke(schema: Json, response: Record<string, string>, onChange: (schema: Json) => void) {
            const json = typeof response.schema === "string" ? JSON.parse(response.schema) : response.schema as Json;
            onChange(json);
        },
    },
    {
        id: "add-action-section",
        name: "Adds an action section",
        prompt: "Adds a new section for the actions",
        parameters: [
            {
                name: "name",
                description: "The name of the new section",
            },
            {
                name: "description",
                description: "The description of the group.",
            },
        ],
        invoke(schema: Json, response: Record<string, string>, onChange: (schema: Json) => void) {
            onChange({
                ...schema,
                actions: [
                    {
                        name: response.name,
                        description: response.description,
                        actions: [],
                    },
                ],
            });
        },
    },
    {
        id: "add-action",
        name: "Adds an action",
        prompt: "Appends the provided action to the schema given the action group Id",
        parameters: [
            {
                name: "groupName",
                description: "The name of the action group you want to add the action to.",
            },
            {
                name: "actionDefinition",
                description: "The schema of the action definition",
            },
        ],
        invoke(schema: Json, response: Record<string, string>, onChange: (schema: Json) => void) {
            const group = (schema.actions as Json[]).find(action => action.name === response.groupName);
            const json = typeof response.actionDefinition === "string"
                ? JSON.parse(response.actionDefinition)
                : response.actionDefinition as Json;
            ((group as Json)?.actions as Json[]).push(json);
            onChange({
                ...schema,
                actions: [
                    ...((schema.actions ?? []) as Json[]),
                ],
            });
        },
    },
    {
        id: "add-api",
        name: "Adds an api",
        prompt: "Appends the provided api to the schema.",
        parameters: [
            {
                name: "api",
                description: "The API definition",
            },
        ],
        invoke(schema: Json, response: Record<string, string>, onChange: (schema: Json) => void) {
            const json = typeof response.api === "string" ? JSON.parse(response.api) : response.api as Json;
            onChange({
                ...schema,
                apis: [
                    ...((schema.apis ?? []) as Json[]),
                    json,
                ],
            });
        },
    },

];

const PROMPT = `You are creating a configuration file for a workflow service.
Follow the users request and always respond with a action that the user needs to take. Combine all changes you wish to propose into a single action.

This is the current configuration of the workflow service:
\`\`\`
{{JSON}}
\`\`\`


Below is an example of an example workflow service configuration that is more full featured
\`\`\`
{{SIMILAR_JSON}}
\`\`\`
`;

/*

The workflow service must very specifically follow the following JSON schema. Anything you propose must be valid JSON as defined by this schema:
\`\`\`
{{SCHEMA}}
\`\`\`
*/

// Create an action and API to create add a hardware asset. It should take the required fields as required and include notes, assigned user, assigned asset (add as an ID), serial number, purchase cost, date, name and location. The location should be a dynamic lookup input field that finds the list of registered locations. Also add an actions that find assets either by name, by asset tag, or by serial number.

// Add a new action group, action, and any related APIs. the action should take a user object, and call the api which returns a list of assets associated with that user.
// Using the Snipe IT apis, create a new workflow service configuration. Put as much information into the schemas as possible based on the example, but do not add any actions yet.
// Modify the schema by adding a new get user api. It should mimic the Okta "User Side Display" and retrieve information about the user and high level information you can pull from snipe-it about assets the user has. It should also link to the snipe it assets for that user as a link.

export default function useAIAssist({
    schema,
    similarSchema,
    onChange,
}: {
    schema: Json,
    similarSchema: Json,
    onChange: (schema: Json) => void,
}) {
    const [showAI, setShow] = useState(false);

    const ref = useRef<Json>(schema);
    const invokeAction = (action: AvailableActionResponseAction) => {
        const handler = AVAILABLE_ACTIONS.find(current => current.id === action.id);
        handler?.invoke(ref.current, action.parameters as Record<string, string>, newSchema => {
            ref.current = newSchema;
            onChange(newSchema);
        });
    };

    useEffect(() => {
        ref.current = schema;
    }, [schema]);

    const prompt = PROMPT
        .replace("{{JSON}}", JSON.stringify(schema))
        .replace("{{SCHEMA}}", JSON.stringify(SCHEMA))
        .replace("{{SIMILAR_JSON}}", JSON.stringify(similarSchema));
    return {
        setShow,
        element: (
            <AIQuestion
                onHide={() => setShow(false)}
                shouldShow={showAI}
                availableActions={AVAILABLE_ACTIONS}
                invokeAction={invokeAction}
                metricPathPrefix={undefined}
                additionalContext={prompt}
                className={classNames("h-auto", Styles["ai-assist"])}
                optionsClassName={Styles["ai-assist-modal"]}
                shouldUseTools={false}
            />
        ),
    };
}
